import WasmController from "react-lib/frameworks/WasmController";

import PatientDetailView from "issara-sdk/apis/PatientDetailView_apps_REG";
import EncounterDetail from "issara-sdk/apis/EncounterDetail_core";
import EncounterPatientListOptimized from "issara-sdk/apis/EncounterPatientListOptimized_core";
import DentalMedicalHistoryByEncounterDetail from "issara-sdk/apis/DentalMedicalHistoryByEncounterDetail_apps_DEN";

import { adToBe } from "react-lib/utils/index";
import { formatUTCtoBEString } from "react-lib/utils/dateUtils";
import moment from "moment";

import { loadDENMaster, DENUserPermission } from "./DENMasterCache";
import { DiagnosisStateInitial, medicalHistoryListInitial } from "./DiagnosisTodayInterface";
import { TreatmentPlanStateInitial, TreatmentTodayStateInitial } from "./TreatmentTodayInterface";
import { DiagramStateInitial } from "./DiagramInterface";
import * as PlaqueRecordI from "./diagram/PlaqueRecordI";
import { getReExamFilterLists } from "./ReExaminationI";
import { HistoryStateInitial } from "./DPOI";
import DentalClinicalFindingHotKeyList from "issara-sdk/apis/DentalClinicalFindingHotKeyList_apps_DEN";

// Props จาก host ข้อมูลอื่น module โหลดเอง
export type DENHostProps = {
  apiToken?: string;
  encounterId?: number | null;
  patientId?: number | null;
  django?: any;
  userPosition?: any;
  currentDoctor?: any;
  selectedDivision?: any;
  userProfile?: any;
  // master data ที่ host มีอยู่แล้ว (ไม่โหลดซ้ำ)
  masterData?: Record<string, any>;
  // CU only
  studentApproval?: boolean;
  autoCheckInOnSave?: boolean;
  languageUX?: string;
};

const CudentStateInitial = {
  isMainLoading: false,

  // message modal
  openModMessage: false,
  messageTitle: "",
  successMessage: null,
  errorMessage: null,
  openModICD10NotFound: false,

  // Re-Examination
  openModalReExamination: false,
  isModalReExaminationLoading: false,
  reExamEncounterList: [],
  allReExamEncounterList: [],
  dateFilterListReExam: [],
  dentistFilterListReExam: [],
  clinicFilterListReExam: [],
  openModalFilterReExamDate: false,
  openModalFilterReExamDentist: false,
  openModalFilterReExamClinic: false,

  // options
  urgencyOptions: [],
  statusExcludeCompleteOptions: [],
  statusIncludeCompleteOptions: [],
  treatmentOptions: [],
  anestheticOptions: [],
  priceChangeReasonOptions: [],
  divisionHasUserOptions: [],

  openClinicalFindingsMenu: false,
  openGallery: {
    open: false,
    activeTabs: [],
    imageOverlayData: [],
    hideBabyTooth: false,
  },
  showClinicalFindingsDetail: false,
  showClinicalFindingTerms: false,
  showClinicalFindingTermsTreatment: false,

  clinicalFindingList: [],
  filterClinicalFindingIds: [],
  clinicalFindingId: null,
  filterItem: null,
  supernumeraryteeth: [],

  activeIndexDiagram: 0,
  activeEvaluation: "",

  // Dental History
  patientFamilyTPList: [],
  ...HistoryStateInitial,

  ...DiagnosisStateInitial,
  ...TreatmentTodayStateInitial,
  ...TreatmentPlanStateInitial,
  ...PlaqueRecordI.StateInitial,
  ...DiagramStateInitial,
};

export const PatientDetailInitial = {
  missingteeth: [],
  partialmissingteeth: [],
  implantteeth: [],
  hideBabyTooth: false,
};

export const StateInitial = {
  ...CudentStateInitial,

  // from host
  django: {} as any,
  userPosition: null as any,
  currentDoctor: null as any,
  selectedDivision: null as any,
  userProfile: {} as any,
  studentApproval: false,
  autoCheckInOnSave: false,

  // master / user
  masterReady: false,
  loadError: null as string | null,
  userPermisstion: null as DENUserPermission | null,

  // patient / encounter
  patientId: null as number | null,
  patientDetail: { ...PatientDetailInitial } as Record<string, any>,
  encounterId: null as number | null,
  encounterInfo: {} as Record<string, any>,
  encounterList: [] as any[],
  encounterIndex: null as number | null,
  PATIENT_DATA: { EMR: { emr_id: null }, ENCOUNTER: { encounter_id: null } } as {
    EMR: { emr_id: number | null };
    ENCOUNTER: { encounter_id: number | null };
  },
  medicalHistoryList: { ...medicalHistoryListInitial } as any,
  isDataSecret: false,
};

export type State = typeof StateInitial & Record<string, any>;

export type Event =
  | { message: "DidMount"; params: DENHostProps }
  | { message: "LoadPatientEncounter"; params: { patientId?: number | null; encounterId?: number | null } }
  | { message: "SetHostProps"; params: DENHostProps };

export type Handler<P = any, R = any> = (controller: WasmController<State, Event, any, any>, params: P) => R;

// django.division อาจเป็น id หรือ object ให้เป็น object เสมอ
export const normalizeDjango = (django: any) => {
  const division = django?.division;

  return {
    ...(django || {}),
    division: division && typeof division === "object" ? division : division ? { id: division } : null,
  };
};

export const DidMount: Handler<DENHostProps> = async (controller, params) => {
  try {
    await DidMountLoad(controller, params);
  } catch (error) {
    console.error("DEN DidMount error: ", error);

    controller.setState({ loadError: "โหลดข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง", isMainLoading: false });
  }
};

// props ของ host ที่เปลี่ยนได้ระหว่างใช้งาน
export const SetHostProps: Handler<DENHostProps> = (controller, params) => {
  controller.setState({
    django: normalizeDjango(params.django),
    userPosition: params.userPosition || null,
    currentDoctor: params.currentDoctor || null,
    selectedDivision: params.selectedDivision || null,
    userProfile: params.userProfile || {},
  });
};

const DidMountLoad: Handler<DENHostProps> = async (controller, params) => {
  controller.setState({
    loadError: null,
    django: normalizeDjango(params.django),
    userPosition: params.userPosition || null,
    currentDoctor: params.currentDoctor || null,
    selectedDivision: params.selectedDivision || null,
    userProfile: params.userProfile || {},
    studentApproval: !!params.studentApproval,
    autoCheckInOnSave: !!params.autoCheckInOnSave,
    masterReady: false,
  });

  // master กับ patient/encounter โหลดพร้อมกัน
  const [master, keyboardShortcut] = await Promise.all([
    loadDENMaster(controller, { userId: params.django?.user?.id, masterData: params.masterData }),
    // hotkey แก้ได้จากหน้านี้ จึงไม่ cache
    GetUserKeyboardShortcut(controller, { user: params.django?.user?.id }),
    LoadPatientEncounter(controller, { patientId: params.patientId, encounterId: params.encounterId }),
  ]);

  controller.data = { ...controller.data, ...master.data };

  const state = controller.getState();

  controller.setState({
    ...(master.options as any),
    doctorProfileData: { ...state.doctorProfileData, keyboardshortcut: keyboardShortcut },
    masterReady: true,
    userPermisstion: master.permission,
    // ค่าเริ่มต้นการแสดงฟันน้ำนมตาม role ทันตแพทย์เด็ก
    patientDetail: { ...state.patientDetail, hideBabyTooth: !master.permission.role_PEDIATRIC_DENTISTS },
    isDataSecret: checkDataSecret(state.patientDetail, master.permission),
  });
};

export const LoadPatientEncounter: Handler<{ patientId?: number | null; encounterId?: number | null }> = async (
  controller,
  params
) => {
  const PATIENT_DATA: State["PATIENT_DATA"] = { EMR: { emr_id: null }, ENCOUNTER: { encounter_id: null } };

  if (!params.patientId) {
    controller.setState({
      patientId: null,
      patientDetail: { ...PatientDetailInitial },
      encounterId: null,
      encounterInfo: {},
      encounterList: [],
      encounterIndex: null,
      PATIENT_DATA,
      medicalHistoryList: { ...medicalHistoryListInitial },
    });

    return;
  }

  controller.setState({ isMainLoading: true });

  const encounterId = params.encounterId || null;

  const [patientDetail, encounterList, encounterInfo, medicalHistoryList] = await Promise.all([
    GetPatientDetail(controller, { patientId: params.patientId }),
    GetEncounterList(controller, { patientId: params.patientId }),
    encounterId ? GetEncounterDetail(controller, { encounterId }) : {},
    encounterId ? GetMedicalHistoryByEncounter(controller, { encounterId }) : { ...medicalHistoryListInitial },
  ]);

  if (encounterInfo?.id) {
    PATIENT_DATA.ENCOUNTER = { encounter_id: encounterInfo.id };
    PATIENT_DATA.EMR = { emr_id: encounterInfo.medical_record || null };
  }

  const encounterIndex = encounterList.findIndex((item: any) => item.id === encounterId);
  const state = controller.getState();

  controller.setState({
    patientId: params.patientId,
    patientDetail: { ...PatientDetailInitial, ...patientDetail, hideBabyTooth: state.patientDetail?.hideBabyTooth },
    encounterId,
    encounterInfo,
    encounterList,
    encounterIndex: encounterIndex === -1 ? null : encounterIndex,
    PATIENT_DATA,
    medicalHistoryList,
    ...(getReExamFilterLists(encounterList) as any),
    isDataSecret: checkDataSecret(patientDetail, state.userPermisstion),
    isMainLoading: false,
  });
};

/* ------------------------------------------------------ */

/*                          Utils                         */

/* ------------------------------------------------------ */
// ผู้ป่วยปกปิด / มีคดีความ และผู้ใช้ไม่มีสิทธิ์ดูเอกสารปกปิด
export const checkDataSecret = (patientDetail: any, permission: DENUserPermission | null) =>
  !permission?.rolegroup_CAN_VIEW_SECRET_DOCUMENT &&
  (patientDetail?.is_secret === "S" || !!patientDetail?.detail?.Lawsuit_active);

/* ------------------------------------------------------ */

/*                           API                          */

/* ------------------------------------------------------ */
const GetUserKeyboardShortcut: Handler<{ user?: number }> = async (controller, params) => {
  if (!params.user) {
    return [];
  }

  const [response, error] = await DentalClinicalFindingHotKeyList.list({
    apiToken: controller.apiToken,
    params: { user: params.user },
  });

  if (error) {
    console.log("DEN GetUserKeyboardShortcut error: ", error);

    return [];
  }

  return response?.items || [];
};

const GetPatientDetail: Handler<{ patientId: number }> = async (controller, params) => {
  const [response, error] = await PatientDetailView.retrieve({
    pk: params.patientId,
    apiToken: controller.apiToken,
    params: {},
  });

  if (error) {
    console.log("DEN GetPatientDetail error: ", error);

    return {};
  }

  return response;
};

const GetEncounterList: Handler<{ patientId: number }> = async (controller, params) => {
  const [response, error] = await EncounterPatientListOptimized.get({
    apiToken: controller.apiToken,
    params: { patient: params.patientId, limit: 99999 },
  });

  if (error) {
    console.log("DEN GetEncounterList error: ", error);

    return [];
  }

  return (response?.items || []).map((item: any) => {
    const [dateCreate, timeCreate] = formatUTCtoBEString(item.created_utc);
    const [dateEdit, timeEdit] = formatUTCtoBEString(item.edited_utc);

    return {
      ...item,
      started: adToBe(moment(item.started).format("YYYY-MM-DD"), "YYYY-MM-DD"),
      created_utc: `${dateCreate} ${timeCreate}`,
      edited_utc: `${dateEdit} ${timeEdit}`,
    };
  });
};

const GetEncounterDetail: Handler<{ encounterId: number }> = async (controller, params) => {
  const [response, error] = await EncounterDetail.retrieve({
    pk: params.encounterId,
    apiToken: controller.apiToken,
    params: {},
  });

  if (error) {
    console.log("DEN GetEncounterDetail error: ", error);

    return {};
  }

  return response;
};

const GetMedicalHistoryByEncounter: Handler<{ encounterId: number }> = async (controller, params) => {
  const [response, error] = await DentalMedicalHistoryByEncounterDetail.retrieve({
    pk: params.encounterId,
    apiToken: controller.apiToken,
  });

  if (error) {
    console.log("DEN GetMedicalHistoryByEncounter error: ", error);

    return { ...medicalHistoryListInitial };
  }

  return response;
};
